/* Eloss — variant picker
 *
 * Spec: §F.4 ("Variant change updates URL, price, availability, media, SKU and
 * variant-scoped metafields via Section Rendering"), §P.5, §P.6.
 *
 * WHY SECTION RENDERING RATHER THAN CLIENT-SIDE PATCHING. Every variant-scoped
 * value on the page — price, compare-at, availability, the quantity ceiling,
 * SKU, variant metafields, which option combinations are still reachable — is
 * computed in Liquid. Re-rendering the section server-side keeps exactly one
 * implementation of each of those rules. Patching them in JavaScript would mean
 * writing a second, silently diverging copy of the buy box.
 *
 * The swap replaces .main-product__info wholesale. That is deliberate:
 *   - Every custom element inside it disconnects and reconnects, so listeners
 *     are released and rebound by the normal lifecycle (the same guarantee the
 *     F-2 fix established for the footer). Nothing accumulates.
 *   - The GALLERY IS NOT REPLACED. It sits outside .main-product__info, keeps
 *     its scroll position and its already-decoded images, and is told to snap
 *     to the new variant's media through the eloss:variant:changed event
 *     (§F.3: "snaps to the variant's media, never filters the gallery").
 *
 * This element is itself inside the replaced subtree. Every DOM write therefore
 * happens LAST, and nothing touches `this` afterwards.
 */

(() => {
  'use strict';

  const Eloss = window.Eloss;
  if (!Eloss) return;

  Eloss.define(
    'eloss-variant-picker',
    class ElossVariantPicker extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.productUrl = this.dataset.productUrl;
        this.variants = this.readVariantMap();
        if (!this.variants.length) return;

        this.onChange = (event) => this.handleChange(event);
        this.addEventListener('change', this.onChange);
      }

      disconnectedCallback() {
        /* Released here so a Theme Editor re-render, or our own section swap,
           can never leave a listener behind on a detached node. */
        if (this.onChange) this.removeEventListener('change', this.onChange);
        if (this.abortController) this.abortController.abort();
      }

      readVariantMap() {
        const script = this.querySelector('[data-variant-map]');
        if (!script) return [];
        try {
          const parsed = JSON.parse(script.textContent);
          return Array.isArray(parsed) ? parsed : [];
        } catch (error) {
          console.error('Eloss: variant map is not valid JSON', error);
          return [];
        }
      }

      /** Selected option values, in option-position order. */
      selectedOptions() {
        const values = [];
        this.querySelectorAll('[data-option-position]').forEach((control) => {
          const position = Number(control.dataset.optionPosition);
          if (!position) return;
          if (control.tagName === 'SELECT') {
            values[position - 1] = control.value;
          } else if (control.checked) {
            values[position - 1] = control.value;
          }
        });
        return values;
      }

      matchVariant(selected) {
        return this.variants.find(
          (variant) =>
            Array.isArray(variant.options) &&
            variant.options.length === selected.length &&
            variant.options.every((value, index) => value === selected[index])
        );
      }

      handleChange(event) {
        const control = event.target.closest('[data-option-position]');
        if (!control) return;

        const selected = this.selectedOptions();
        const variant = this.matchVariant(selected);

        if (!variant) {
          /* An unreachable combination. The server already marks these
             aria-disabled; if one is still chosen (a select, or assistive
             tech), fail loudly in the buy box rather than silently. */
          this.markUnavailable();
          return;
        }

        this.render(variant);
      }

      markUnavailable() {
        const root = document.querySelector(
          `.main-product[data-section-id="${this.sectionId}"]`
        );
        const submit = root && root.querySelector('.product-form__submit');
        if (!submit) return;
        submit.disabled = true;
      }

      async render(variant) {
        /* One request in flight at a time: a fast clicker must land on the
           variant they chose last, not whichever response returns last. */
        if (this.abortController) this.abortController.abort();
        this.abortController = new AbortController();

        const sectionId = this.sectionId;
        const productUrl = this.productUrl;
        const url = `${productUrl}?variant=${variant.id}&section_id=${sectionId}`;

        try {
          const response = await fetch(url, {
            signal: this.abortController.signal,
          });
          if (!response.ok) throw new Error(`HTTP ${response.status}`);

          const html = await response.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');

          const nextInfo = doc.querySelector('.main-product__info');
          const nextSticky = doc.querySelector('.sticky-atc');

          /* DOMParser parses with scripting DISABLED, so it materialises the
             children of <noscript> as real elements rather than as inert text.
             Inserting that subtree into a live document turns the no-JS
             <select name="id"> into a second, submitting form control, and the
             form starts posting `id` twice. We are demonstrably running, so the
             no-JS fallback must not survive the swap. */
          if (nextInfo) {
            nextInfo.querySelectorAll('noscript').forEach((el) => el.remove());
          }
          if (nextSticky) {
            nextSticky.querySelectorAll('noscript').forEach((el) => el.remove());
          }

          const root = document.querySelector(
            `.main-product[data-section-id="${sectionId}"]`
          );
          if (!root || !nextInfo) return;

          /* §F.4 — the URL reflects the selection. replaceState, not
             pushState: choosing three variants in a row should not cost the
             visitor three presses of Back to leave the page. */
          const nextUrl = `${productUrl}?variant=${variant.id}`;
          window.history.replaceState({}, '', nextUrl);

          /* Announce BEFORE the swap, so the gallery — which lives outside the
             replaced subtree and survives it — can snap while the old nodes are
             still addressable. */
          Eloss.emit(Eloss.EVENTS.variantChanged, {
            variantId: variant.id,
            available: variant.available,
            sectionId,
          });

          /* Read the focused option BEFORE the swap: replacing the subtree
             destroys the focused radio and the browser drops focus to <body>,
             which strands a keyboard user at the top of the document on every
             arrow-key change. */
          const focusedInput = document.activeElement;
          const focusedValue = focusedInput && focusedInput.matches('.variant-option__input')
            ? focusedInput.value
            : null;

          const currentInfo = root.querySelector('.main-product__info');
          if (currentInfo) currentInfo.replaceWith(nextInfo);

          if (focusedValue !== null) {
            const restored = Array.from(
              nextInfo.querySelectorAll('.variant-option__input')
            ).find((input) => input.value === focusedValue);
            /* preventScroll: the page must not jump as a side effect of
               putting focus back. */
            if (restored) restored.focus({ preventScroll: true });
          }

          const currentSticky = root.querySelector('.sticky-atc');
          if (currentSticky && nextSticky) {
            currentSticky.replaceWith(nextSticky);
          }
        } catch (error) {
          if (error.name === 'AbortError') return;
          console.error('Eloss: variant render failed', error);
          Eloss.emit(Eloss.EVENTS.cartError, {
            scope: 'variant',
            message: String(error),
          });
        }
      }
    }
  );
})();
